import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Fade,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Psychology as PsychologyIcon,
  Analytics as AnalyticsIcon,
  History as HistoryIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Celebration as CelebrationIcon
} from '@mui/icons-material';

// Флаг для включения/отключения onboarding
const ONBOARDING_ENABLED = false; // Установить true чтобы включить

const ONBOARDING_KEY_PREFIX = 'onboarding_completed_user_';

const steps = [
  {
    title: 'Добро пожаловать! 👋',
    description: 'Это система анализа компаний с помощью искусственного интеллекта. Давайте познакомимся с основными возможностями.',
    icon: CelebrationIcon,
    highlight: null
  },
  {
    title: 'Поиск компаний',
    description: 'Введите название города и укажите количество компаний для анализа. Система найдёт магазины строительных инструментов в выбранном городе.',
    icon: SearchIcon,
    highlight: 'search-form'
  },
  {
    title: 'ИИ-анализ',
    description: 'Каждая найденная компания автоматически анализируется искусственным интеллектом. Вы получите оценку потенциала, рекомендации по работе и контактную информацию.',
    icon: PsychologyIcon,
    highlight: null
  },
  {
    title: 'Работа с результатами',
    description: 'После анализа вы можете отмечать статусы компаний, экспортировать данные в CRM и оставлять комментарии.',
    icon: AnalyticsIcon,
    highlight: null
  },
  {
    title: 'История поисков',
    description: 'Все ваши поиски сохраняются. Вы можете вернуться к результатам в любое время через меню "История".',
    icon: HistoryIcon,
    highlight: null
  }
];

const Onboarding = ({ onComplete, userId }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    // Если onboarding отключен, ничего не делаем
    if (!ONBOARDING_ENABLED) return;
    
    // Проверяем, проходил ли пользователь onboarding
    if (!userId) return; // Не показываем пока userId не загружен
    
    const onboardingKey = `${ONBOARDING_KEY_PREFIX}${userId}`;
    const completed = localStorage.getItem(onboardingKey);
    if (!completed) {
      // Небольшая задержка для плавного появления
      const timer = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, [userId]);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleComplete = () => {
    if (userId) {
      const onboardingKey = `${ONBOARDING_KEY_PREFIX}${userId}`;
      localStorage.setItem(onboardingKey, 'true');
    }
    setOpen(false);
    onComplete?.();
  };

  const handleSkip = () => {
    if (userId) {
      const onboardingKey = `${ONBOARDING_KEY_PREFIX}${userId}`;
      localStorage.setItem(onboardingKey, 'true');
    }
    setOpen(false);
    onComplete?.();
  };

  const currentStep = steps[activeStep];
  const IconComponent = currentStep.icon;

  // Если onboarding отключен, не показываем ничего
  if (!ONBOARDING_ENABLED) return null;

  if (!open) return null;

  return (
    <>
      {/* Затемнённый фон */}
      <Fade in={open}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={(e) => e.target === e.currentTarget && handleSkip()}
        >
          {/* Основная карточка */}
          <Fade in={open} timeout={300}>
            <Paper
              elevation={24}
              sx={{
                maxWidth: 520,
                width: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {/* Заголовок с градиентом */}
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white',
                  p: 3,
                  pb: 4,
                  position: 'relative'
                }}
              >
                <IconButton
                  onClick={handleSkip}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: 'white',
                    opacity: 0.7,
                    '&:hover': { opacity: 1 }
                  }}
                >
                  <CloseIcon />
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: alpha('#fff', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <IconComponent sx={{ fontSize: 32 }} />
                  </Box>
                  <Typography variant="h5" fontWeight={600}>
                    {currentStep.title}
                  </Typography>
                </Box>
              </Box>

              {/* Контент */}
              <Box sx={{ p: 3 }}>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ 
                    minHeight: 80,
                    lineHeight: 1.7,
                    fontSize: '1.05rem'
                  }}
                >
                  {currentStep.description}
                </Typography>

                {/* Stepper */}
                <Stepper 
                  activeStep={activeStep} 
                  alternativeLabel 
                  sx={{ 
                    mt: 3, 
                    mb: 3,
                    '& .MuiStepLabel-label': {
                      fontSize: '0.75rem'
                    }
                  }}
                >
                  {steps.map((step, index) => (
                    <Step key={index}>
                      <StepLabel />
                    </Step>
                  ))}
                </Stepper>

                {/* Кнопки навигации */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    onClick={handleSkip}
                    color="inherit"
                    sx={{ color: 'text.secondary' }}
                  >
                    Пропустить
                  </Button>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {activeStep > 0 && (
                      <Button
                        onClick={handleBack}
                        startIcon={<ArrowBackIcon />}
                        variant="outlined"
                      >
                        Назад
                      </Button>
                    )}
                    <Button
                      onClick={handleNext}
                      endIcon={activeStep === steps.length - 1 ? null : <ArrowForwardIcon />}
                      variant="contained"
                      sx={{
                        px: 3,
                        background: activeStep === steps.length - 1 
                          ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                          : undefined
                      }}
                    >
                      {activeStep === steps.length - 1 ? 'Начать работу! 🚀' : 'Далее'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Box>
      </Fade>
    </>
  );
};

// Функция для сброса onboarding (для тестирования)
export const resetOnboarding = (userId) => {
  if (userId) {
    const onboardingKey = `${ONBOARDING_KEY_PREFIX}${userId}`;
    localStorage.removeItem(onboardingKey);
  }
};

// Функция для проверки, был ли пройден onboarding
export const isOnboardingCompleted = (userId) => {
  if (!userId) return true; // Не показываем если userId неизвестен
  const onboardingKey = `${ONBOARDING_KEY_PREFIX}${userId}`;
  return localStorage.getItem(onboardingKey) === 'true';
};

export default Onboarding;

